import React, { useState } from 'react';
import { Button, Card, Col, Collapse, Form, Row } from 'react-bootstrap';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { Table, Thead, Tbody, Tr, Th, Td } from 'react-super-responsive-table';
import CardMobile from './CardMobile';
import { formatCurrency } from '../utils';
import AuthStorage from '../../../Utils/AuthStorage';

// Estilo inline de Th sticky — `position: sticky` em <th> precisa de background
// sólido (senão linhas scrollam por baixo) e top:0 para fixar no topo do scroll.
const stickyTh = {
  position: 'sticky',
  top: 0,
  background: '#f8f9fa',
  zIndex: 2,
  borderBottom: '2px solid #dee2e6',
};

// W-0075 (devolução pt.2): cor do badge por tipo de parcela na lista unificada.
const tipoCor = (tipo) => {
  if (tipo === 'Sinal') return '#6c757d';
  if (tipo === 'Condicionante') return '#fd7e14';
  if (tipo === 'Intermediária') return '#6f42c1';
  if (tipo === 'Programada') return '#198754';
  if (tipo === 'Financiamento') return '#0f334f';
  return '#495057';
};

export default function PainelAntecipacao(props) {
  const antecipacao = props.antecipacao;
  const onSimular = props.onSimular;
  const onSimularPorValor = props.onSimularPorValor;
  const onLimpar = props.onLimpar;
  const resultado = props.resultado;

  // W-0075 (devolução SINAL pt.2): corretor não pode antecipar "do início" — só "do fim".
  // Demais perfis (gerente/admin/CAV) continuam vendo ambas as opções.
  const isCorretor = AuthStorage.isCorretor();

  const [qtd, setQtd] = useState('');
  const [valorAntecipar, setValorAntecipar] = useState('');
  // Painel inicia colapsado — o sumário top5/10/15 já vive no Resumo Final.
  // Aqui é detalhado (tabela parcela a parcela + simulação Original/Desconto/Novo).
  const [aberto, setAberto] = useState(false);
  // Ordenação por coluna da tabela de parcelas. null = ordem original (cronológica).
  // Declarado aqui (antes do early return) para não violar as regras de hooks.
  const [ordenacao, setOrdenacao] = useState(null); // { coluna, dir: 'asc' | 'desc' }

  if (!antecipacao) return null;

  const parcelasSelecionadas = resultado
    ? { ini: resultado.indiceInicio, fim: resultado.indiceFim }
    : null;

  const isSelecionada = (idx) =>
    parcelasSelecionadas && idx >= parcelasSelecionadas.ini && idx <= parcelasSelecionadas.fim;

  const simular = (direcao) => {
    if (!onSimular) return;
    onSimular(parseInt(qtd, 10), direcao);
  };

  const simularPorValor = (direcao) => {
    if (!onSimularPorValor) return;
    const num = parseFloat(String(valorAntecipar).replace(',', '.'));
    if (!num || num <= 0) return;
    onSimularPorValor(num, direcao);
  };

  const limpar = () => {
    setQtd('');
    setValorAntecipar('');
    if (onLimpar) onLimpar();
  };

  // 3º clique na mesma coluna volta à ordem original (cronológica).
  const ordenarPor = (coluna) => {
    setOrdenacao((prev) => {
      if (!prev || prev.coluna !== coluna) return { coluna, dir: 'asc' };
      if (prev.dir === 'asc') return { coluna, dir: 'desc' };
      return null;
    });
  };

  const valorColuna = (item, coluna) => {
    switch (coluna) {
      case 'numero': return item.numero || 0;
      case 'tipo': return item.tipoLabel || item.tipo || '';
      case 'vencimento': return item.vencimento ? new Date(item.vencimento).getTime() : 0;
      case 'valorOriginal': return item.valorOriginal || 0;
      case 'desconto': return Math.round((item.valorOriginal - item.valorPresente) * 100) / 100;
      case 'valorPresente': return item.valorPresente || 0;
      default: return 0;
    }
  };

  const setaSort = (coluna) =>
    ordenacao && ordenacao.coluna === coluna ? (ordenacao.dir === 'asc' ? ' ▲' : ' ▼') : '';

  // Mantém o índice original (idx) p/ o highlight de antecipação (isSelecionada usa
  // posição cronológica via indiceInicio/Fim — não pode mudar ao ordenar a exibição).
  const linhasParcelas = (antecipacao.parcelas || []).map((item, idx) => ({ item, idx }));
  const linhasOrdenadas = ordenacao
    ? linhasParcelas.slice().sort((a, b) => {
        const va = valorColuna(a.item, ordenacao.coluna);
        const vb = valorColuna(b.item, ordenacao.coluna);
        const cmp = typeof va === 'string' ? va.localeCompare(vb) : va - vb;
        return ordenacao.dir === 'asc' ? cmp : -cmp;
      })
    : linhasParcelas;

  const thOrdenavel = Object.assign({ cursor: 'pointer', userSelect: 'none' }, stickyTh);

  return (
    <Card className="mb-2">
      <Card.Body className="py-2">
        <div
          className="d-flex justify-content-between align-items-center"
          onClick={() => setAberto((v) => !v)}
          style={{ cursor: 'pointer' }}
        >
          <h6 className="mb-0 text-primary">
            <strong>Antecipação de Parcelas</strong>
            <span className="text-muted small ml-2">
              · 5x R$ {formatCurrency(antecipacao.top5)} · 10x R$ {formatCurrency(antecipacao.top10)} · 15x R$ {formatCurrency(antecipacao.top15)}
            </span>
          </h6>
          {aberto
            ? <FaChevronUp size={14} className="text-primary" />
            : <FaChevronDown size={14} className="text-primary" />}
        </div>

        <Collapse in={aberto}>
          <div className="mt-2">

            {/* Resumo da simulação ativa — padrão Área do Cliente */}
            {resultado && (
              <div className="alert alert-light border py-2 mb-2">
                <div className="small text-muted mb-1">
                  {resultado.modo === 'valor' ? (
                    <>
                      Simulando antecipação por valor de{' '}
                      <strong>R$ {formatCurrency(resultado.valorInformado)}</strong>{' '}
                      {resultado.direcao === 'inicio' ? 'a partir do início' : 'a partir do fim'}:{' '}
                      <strong>{resultado.qtdQuitadas}</strong>{' '}
                      {resultado.qtdQuitadas === 1 ? 'parcela quitada' : 'parcelas quitadas'},
                      restando <strong>R$ {formatCurrency(resultado.sobra)}</strong>
                      {resultado.faltaProxima > 0 && (
                        <>
                          . Falta <strong>R$ {formatCurrency(resultado.faltaProxima)}</strong>{' '}
                          para incluir a próxima.
                        </>
                      )}
                      {resultado.qtdQuitadas === 0 && (
                        <> — valor insuficiente para quitar uma parcela.</>
                      )}
                    </>
                  ) : (
                    <>
                      Simulando antecipação de{' '}
                      <strong>{resultado.qtd}</strong>{' '}
                      {resultado.qtd === 1 ? 'parcela' : 'parcelas'}{' '}
                      {resultado.direcao === 'inicio' ? 'do início' : 'do fim'}.
                    </>
                  )}
                </div>
                <Row className="text-center">
                  <Col xs={12} md={4} className="mb-2 mb-md-0">
                    <small className="d-block text-muted">Valor original</small>
                    <strong className="text-danger" style={{ fontSize: '1.1rem' }}>
                      R$ {formatCurrency(resultado.valorOriginal)}
                    </strong>
                  </Col>
                  <Col xs={12} md={4} className="mb-2 mb-md-0">
                    <small className="d-block text-muted">Valor desconto</small>
                    <strong className="text-primary" style={{ fontSize: '1.1rem' }}>
                      - R$ {formatCurrency(resultado.desconto)}
                    </strong>
                  </Col>
                  <Col xs={12} md={4}>
                    <small className="d-block text-muted">Valor novo</small>
                    <strong className="text-success" style={{ fontSize: '1.1rem' }}>
                      R$ {formatCurrency(resultado.valorNovo)}
                    </strong>
                  </Col>
                </Row>
              </div>
            )}

            {/* Modo 1: simulação por QUANTIDADE de parcelas */}
            <Row className="mb-2 align-items-end">
              <Col xs={12} md={4}>
                <Form.Group className="mb-2 mb-md-0">
                  <Form.Label className="small mb-1">Qtd. de parcelas</Form.Label>
                  <Form.Control
                    size="sm"
                    type="number"
                    min="1"
                    max={antecipacao.parcelas ? antecipacao.parcelas.length : undefined}
                    value={qtd}
                    onChange={(e) => setQtd(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col xs={12} md={8}>
                <div className="d-flex flex-column flex-md-row">
                  {!isCorretor && (
                    <Button
                      size="sm"
                      variant="outline-primary"
                      className="mr-md-2 mb-2 mb-md-0"
                      onClick={() => simular('inicio')}
                      disabled={!qtd}
                    >
                      Simular do início
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline-primary"
                    className="mr-md-2 mb-2 mb-md-0"
                    onClick={() => simular('fim')}
                    disabled={!qtd}
                  >
                    Simular do fim
                  </Button>
                </div>
              </Col>
            </Row>

            {/* Modo 2: simulação por VALOR (W-0075 devolução) */}
            <Row className="mb-2 align-items-end">
              <Col xs={12} md={4}>
                <Form.Group className="mb-2 mb-md-0">
                  <Form.Label className="small mb-1">Valor a antecipar (R$)</Form.Label>
                  <Form.Control
                    size="sm"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="Ex.: 1000"
                    value={valorAntecipar}
                    onChange={(e) => setValorAntecipar(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col xs={12} md={8}>
                <div className="d-flex flex-column flex-md-row">
                  {!isCorretor && (
                    <Button
                      size="sm"
                      variant="outline-success"
                      className="mr-md-2 mb-2 mb-md-0"
                      onClick={() => simularPorValor('inicio')}
                      disabled={!valorAntecipar}
                    >
                      Simular do início (por valor)
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline-success"
                    className="mr-md-2 mb-2 mb-md-0"
                    onClick={() => simularPorValor('fim')}
                    disabled={!valorAntecipar}
                  >
                    Simular do fim (por valor)
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={limpar}
                    disabled={!qtd && !valorAntecipar && !resultado}
                  >
                    Limpar
                  </Button>
                </div>
              </Col>
            </Row>

            {antecipacao.parcelas && antecipacao.parcelas.length > 0 && (
              <>
                <div className="d-none d-md-block">
                  <div className="border" style={{ maxHeight: 320, overflowY: 'auto' }}>
                    <Table className="table table-sm table-striped table-borderless mb-0">
                      <Thead>
                        <Tr>
                          <Th className="text-center" style={thOrdenavel} onClick={() => ordenarPor('numero')}>#{setaSort('numero')}</Th>
                          <Th style={thOrdenavel} onClick={() => ordenarPor('tipo')}>Tipo{setaSort('tipo')}</Th>
                          <Th className="text-center" style={thOrdenavel} onClick={() => ordenarPor('vencimento')}>Vencimento{setaSort('vencimento')}</Th>
                          <Th className="text-center" style={thOrdenavel} onClick={() => ordenarPor('valorOriginal')}>Valor Original{setaSort('valorOriginal')}</Th>
                          <Th className="text-center" style={thOrdenavel} onClick={() => ordenarPor('desconto')}>Desconto{setaSort('desconto')}</Th>
                          <Th className="text-center" style={thOrdenavel} onClick={() => ordenarPor('valorPresente')}>Valor Antecipado{setaSort('valorPresente')}</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {linhasOrdenadas.map(({ item, idx }) => {
                          const desconto = Math.round((item.valorOriginal - item.valorPresente) * 100) / 100;
                          const selecionada = isSelecionada(idx);
                          // W-0075 (devolução SINAL pt.3): linha verde + disabled
                          // para parcelas não-antecipáveis (Sinal à vista ≠ Boleto).
                          // Verde tem prioridade sobre vermelho de seleção.
                          const naoAntecipavel = item.antecipavel === false;
                          let rowStyle = null;
                          if (naoAntecipavel) {
                            rowStyle = { background: '#d4edda', color: '#155724', opacity: 0.7 };
                          } else if (selecionada) {
                            rowStyle = { background: '#fdecea', color: '#b71c1c', fontWeight: 600 };
                          }
                          return (
                            <Tr key={item.numero} style={rowStyle}>
                              <Td className="text-center">{item.numero}</Td>
                              <Td>
                                <span
                                  className="badge"
                                  style={{
                                    background: tipoCor(item.tipo),
                                    color: '#fff',
                                    fontSize: '10px',
                                    padding: '2px 6px',
                                  }}
                                >
                                  {item.tipoLabel || item.tipo}
                                </span>
                              </Td>
                              <Td className="text-center">
                                {item.vencimento ? new Date(item.vencimento).toLocaleDateString('pt-BR') : '-'}
                              </Td>
                              <Td className="text-center">
                                R$ {formatCurrency(item.valorOriginal)}
                              </Td>
                              <Td className="text-center">
                                {desconto > 0 ? '- R$ ' + formatCurrency(desconto) : 'R$ 0,00'}
                              </Td>
                              <Td className="text-center">
                                R$ {formatCurrency(item.valorPresente)}
                              </Td>
                            </Tr>
                          );
                        })}
                      </Tbody>
                    </Table>
                  </div>
                </div>

                <div
                  className="d-block d-md-none"
                  style={{ maxHeight: 400, overflowY: 'auto' }}
                >
                  {linhasOrdenadas.map(({ item, idx }) => {
                    const desconto = Math.round((item.valorOriginal - item.valorPresente) * 100) / 100;
                    const selecionada = isSelecionada(idx);
                    const naoAntecipavel = item.antecipavel === false;
                    let mobileBoxStyle = null;
                    if (naoAntecipavel) {
                      mobileBoxStyle = { background: '#d4edda', border: '1px solid #c3e6cb', borderRadius: 4, marginBottom: 8, opacity: 0.75 };
                    } else if (selecionada) {
                      mobileBoxStyle = { background: '#fdecea', border: '1px solid #b71c1c', borderRadius: 4, marginBottom: 8 };
                    }
                    return (
                      <div
                        key={item.numero}
                        style={mobileBoxStyle}
                      >
                        <CardMobile
                          titulo={
                            <>
                              <span
                                className="badge mr-1"
                                style={{
                                  background: tipoCor(item.tipo),
                                  color: '#fff',
                                  fontSize: '10px',
                                  padding: '2px 6px',
                                }}
                              >
                                {item.tipoLabel || item.tipo}
                              </span>
                              {'Parcela ' + item.numero}
                            </>
                          }
                          destaque={'R$ ' + formatCurrency(item.valorPresente)}
                          campos={[
                            {
                              label: 'Vencimento',
                              valor: item.vencimento ? new Date(item.vencimento).toLocaleDateString('pt-BR') : '-',
                            },
                            {
                              label: 'Valor Original',
                              valor: 'R$ ' + formatCurrency(item.valorOriginal),
                            },
                            {
                              label: 'Desconto',
                              valor: desconto > 0 ? '- R$ ' + formatCurrency(desconto) : 'R$ 0,00',
                            },
                          ]}
                        />
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </Collapse>
      </Card.Body>
    </Card>
  );
}
